import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Paper from '@mui/material/Paper';

import { IngestionSettings } from '../components/settings/IngestionSettings';
import { BrandingSettings } from '../components/settings/BrandingSettings';
import { AISettings } from '../components/settings/AISettings';
import { RetentionSettings } from '../components/settings/RetentionSettings';
import { DigestSettings } from '../components/settings/DigestSettings';
import { TenantConfiguration } from '../components/settings/TenantConfiguration';
import { CadSettings } from '../components/settings/CadSettings';
import { authService } from '../services/authService';
import { demoAuthService } from '../services/demoAuthService';
import { DemoContactRedirect } from '../components/demo/DemoContactRedirect';
import { isDemoMode } from '../config';
import { apiClient } from '../services/apiClient';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `settings-tab-${index}`,
    'aria-controls': `settings-tabpanel-${index}`,
  };
}

/**
 * The tabs, in order, in one place. Both the tab strip and the panels are rendered from this, so a
 * tab cannot be added to one and not the other, and the ?tenant= deep link resolves by name. It
 * used to hard-code 4, which stopped being Tenant Configuration the moment the QA Digest tab was
 * added and silently opened the wrong tab.
 */
type TabKey = 'ingestion' | 'branding' | 'ai' | 'retention' | 'digest' | 'cad' | 'tenant';

interface SettingsTab {
  key: TabKey;
  label: string;
  /** `openTab` lets a tab send the admin to another one, for a setting the two share. */
  render: (openTab: (key: TabKey) => void) => React.ReactNode;
}

const TABS: SettingsTab[] = [
  { key: 'ingestion', label: 'Ingestion', render: () => <IngestionSettings /> },
  { key: 'branding', label: 'Branding', render: () => <BrandingSettings /> },
  {
    key: 'ai',
    label: 'AI Settings',
    render: (openTab) => <AISettings onOpenCadSettings={() => openTab('cad')} />,
  },
  { key: 'retention', label: 'Retention', render: () => <RetentionSettings /> },
  { key: 'digest', label: 'QA Digest', render: () => <DigestSettings /> },
  { key: 'cad', label: 'CAD Records', render: () => <CadSettings /> },
  { key: 'tenant', label: 'Tenant Configuration', render: () => <TenantConfiguration /> },
];

const tabIndex = (key: TabKey) => TABS.findIndex((t) => t.key === key);

export const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tabValue, setTabValue] = useState(0);
  const [roleChecked, setRoleChecked] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);

  // If a tenant query param is present, default to the Tenant Configuration tab
  const hasTenantParam = !!searchParams.get('tenant');
  useEffect(() => {
    if (hasTenantParam) {
      setTabValue(tabIndex('tenant'));
      // When PlatformAdmin views a specific tenant, set the tenant header
      // so tenant-scoped API calls (Ingestion, Branding, AI) resolve correctly
      const tenantId = searchParams.get('tenant');
      if (tenantId) {
        apiClient.defaults.headers.common['X-Tenant-Id'] = tenantId;
      }
    }
    // Cleanup: remove tenant header when leaving settings
    return () => {
      delete apiClient.defaults.headers.common['X-Tenant-Id'];
    };
  }, [hasTenantParam, searchParams]);

  // Check user permissions for settings access
  useEffect(() => {
    // Demo mode: use demo auth service
    if (isDemoMode && !demoAuthService.canAccessSettings()) {
      navigate('/demo/settings', { replace: true });
      return;
    }

    // Production/Real auth: check for PlatformAdmin role from DB
    if (!isDemoMode) {
      authService.getUserAsync().then(user => {
        if (!user || user.role !== 'PlatformAdmin') {
          navigate('/', { replace: true });
        } else {
          // PlatformAdmin without a tenant param — redirect to admin landing
          if (!hasTenantParam) {
            navigate('/admin', { replace: true });
            return;
          }
          setHasAccess(true);
        }
        setRoleChecked(true);
      });
      return;
    }
    setRoleChecked(true);
    setHasAccess(true);
  }, [navigate]);

  // If demo user without settings access, show contact redirect
  if (isDemoMode && !demoAuthService.canAccessSettings()) {
    return <DemoContactRedirect feature="settings" />;
  }

  // If production user without PlatformAdmin role, don't render (redirect will happen)
  if (!isDemoMode && (!roleChecked || !hasAccess)) {
    return null;
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Settings
      </Typography>
      
      <Paper sx={{ mt: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="settings tabs">
            {TABS.map((tab, index) => (
              <Tab key={tab.key} label={tab.label} {...a11yProps(index)} />
            ))}
          </Tabs>
        </Box>

        {TABS.map((tab, index) => (
          <TabPanel key={tab.key} value={tabValue} index={index}>
            {tab.render((key) => setTabValue(tabIndex(key)))}
          </TabPanel>
        ))}
      </Paper>
    </Box>
  );
};